/**
 * Places Wave A — pure work-gap chips (Soft≠Live).
 * Off-map / off-ring / ungrouped / mismatch / conflict — read-only honesty.
 */
import { pointInZone } from "./zone-mini-map.js";

/** Pure: rings map label → closed lng/lat ring from surveyed zone rows. */
export function placesRingsOf(surveyed) {
  const out = {};
  for (const z of Array.isArray(surveyed) ? surveyed : []) {
    const label = String((z && z.label) || "").trim();
    const ring = z && z.ring;
    if (!label || !Array.isArray(ring) || ring.length < 3) continue;
    const closed = ring[0] === ring[ring.length - 1] ? ring : [...ring, ring[0]];
    out[label] = closed;
  }
  return out;
}

/** Pure: which ring labels contain this lng/lat pin (same-site rings only). */
export function placesPinRingLabelsOf(ll, rings) {
  if (!ll || !Array.isArray(ll) || ll.length < 2) return [];
  const hit = [];
  for (const [label, ring] of Object.entries(rings || {})) {
    if (pointInZone(ll, ring)) hit.push(label);
  }
  return hit;
}

/**
 * Pure: work-gap counts for Places chrome.
 * devices = fleetZoneDevicesOf rows already filtered to working site.
 * surveyed = fleetZoneListOf rows.
 */
export function placesWorkGapsOf(devices, surveyed) {
  const rows = Array.isArray(devices) ? devices : [];
  const zones = Array.isArray(surveyed) ? surveyed : [];
  const rings = placesRingsOf(zones);
  let offMap = 0;
  let offRing = 0;
  let ungrouped = 0;
  let conflict = 0;
  const mismatch = new Set();
  for (const z of zones) {
    for (const id of z.mismatch || []) {
      const s = String(id || "").trim();
      if (s) mismatch.add(s);
    }
  }
  for (const d of rows) {
    if (!d) continue;
    if (!String(d.group || "").trim()) ungrouped += 1;
    if (!d.ll) {
      offMap += 1;
      continue;
    }
    const labels = placesPinRingLabelsOf(d.ll, rings);
    if (!labels.length) offRing += 1;
    if (labels.length >= 2) conflict += 1;
  }
  return {
    offMap,
    offRing,
    ungrouped,
    conflict,
    mismatch: mismatch.size,
    zoneCount: Object.keys(rings).length,
    deviceCount: rows.length,
    groupCount: new Set(
      rows.map((d) => String((d && d.group) || "").trim()).filter(Boolean),
    ).size,
  };
}

/** Pure: Preferred crew options from Roster rows — never Machine Group names. */
export function placesPreferredCrewOptionsOf(personnel, saved) {
  const rows = Array.isArray(personnel) ? personnel : [];
  const seen = new Set();
  const out = [];
  for (const row of rows) {
    /* Soft≠Live: crew/team only — never Machine Group / ink keys. */
    const crew = String(
      (row && (row.crew || row.team || row.preferred_crew || row.roster_crew)) || "",
    ).trim();
    if (!crew) continue;
    const key = crew.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ value: crew, label: crew, hint: "Roster crew tag" });
  }
  out.sort((a, b) => a.value.localeCompare(b.value));
  const keep = String(saved || "").trim();
  if (keep && !seen.has(keep.toLowerCase())) {
    out.unshift({ value: keep, label: `${keep} · saved`, hint: "Saved on server" });
  }
  return out;
}

/** Stable palette — zones cool, sites warm — so who-is-who reads at a glance. */
const _ZONE_TONES = ["#3DE1C8", "#7FD1FF", "#A78BFA", "#5EEAD4", "#67E8F9", "#C4B5FD", "#86EFAC", "#38BDF8"];
const _SITE_TONES = ["#FFB65C", "#FF9B7A", "#F0ABFC", "#FBBF24", "#FB7185", "#FDBA74", "#E879F9", "#F472B6"];

export function placesEntityToneOf(name, kind = "zone") {
  const s = String(name || "").trim();
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  const palette = kind === "site" ? _SITE_TONES : _ZONE_TONES;
  return palette[(h >>> 0) % palette.length];
}
